import { create, useStore as useZustandStore, type StateCreator, type StoreApi } from 'zustand';
import { useProjectsStore } from './projects';

// Global registry for project-specific store instances
// Map<StoreName, Map<ProjectId, StoreApi>>
const storesByProject = new Map<string, Map<string, StoreApi<any>>>();

/**
 * Factory function to create a project-scoped Zustand store.
 * 
 * It creates a proxy hook that dynamically uses the store instance
 * corresponding to the currently active project ID.
 * 
 * @param name A unique name for the store (e.g., 'tasks', 'files').
 * @param initializer The state creator function, same as for `create`.
 * @returns A custom hook for accessing the project-scoped store, with `getState` and `setState` attached.
 */
export function createProjectScopedStore<T extends object>(
  name: string,
  initializer: StateCreator<T>
) {
  /**
   * Gets or creates the store API for a given project ID.
   * @param projectId The ID of the project, or 'global' for no active project.
   */
  const getStoreApi = (projectId: string | null): StoreApi<T> => {
    const projectKey = projectId || 'global';

    if (!storesByProject.has(name)) {
      storesByProject.set(name, new Map());
    }
    const storeRegistry = storesByProject.get(name)!;

    if (!storeRegistry.has(projectKey)) {
      // Create and cache a new store instance for this project
      storeRegistry.set(projectKey, create(initializer));
    }
    return storeRegistry.get(projectKey)!;
  };

  // This is the hook that components will use. It automatically selects the
  // right store based on the active project and subscribes the component to it.
  function useProjectStore<U>(
    selector: (state: T) => U,
    equalityFn?: (a: U, b: U) => boolean
  ): U {
    const activeProjectId = useProjectsStore(state => state.activeProjectId);
    const storeApi = getStoreApi(activeProjectId);
    return useZustandStore(storeApi, selector, equalityFn);
  }

  // Attach non-hook methods to the hook itself for external access.
  Object.assign(useProjectStore, {
    getState: () => {
      const activeProjectId = useProjectsStore.getState().activeProjectId;
      return getStoreApi(activeProjectId).getState();
    },
    setState: (
      partial: T | Partial<T> | ((state: T) => T | Partial<T>),
      replace?: boolean
    ) => {
      const activeProjectId = useProjectsStore.getState().activeProjectId;
      return getStoreApi(activeProjectId).setState(partial, replace);
    },
    subscribe: (listener: (state: T, prevState: T) => void) => {
      const activeProjectId = useProjectsStore.getState().activeProjectId;
      return getStoreApi(activeProjectId).subscribe(listener);
    },
  });

  return useProjectStore as typeof useProjectStore & Pick<StoreApi<T>, 'getState' | 'setState' | 'subscribe'>;
}